import { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import type { ProduccionCabecera, PlanificacionSemanalRow } from '@/types'
import { fmt, fmtUds, pct, parseDate, sd } from '@/utils/planningFormatters'
import { ConsumoSubTable } from './ConsumoSubTable'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import { BotonMarca, useMarcada, useMarcasFilas } from '@/components/common/MarcasFilas'
import { claveFila } from '@/utils/marcasFilas'
import { cuentaComoRotura } from '@/utils/estadoMaterial'

function fmtDate(v: string | null | undefined): string {
  const d = parseDate(v)
  if (!d) return '—'
  return `${d.getDate()}/${d.getMonth() + 1}`
}

export function OrderCard({ prod, items, dark, onSelectComponent, highlighted, autoAbrir, resalta, busquedaNonce }: {
  prod: ProduccionCabecera
  items: PlanificacionSemanalRow[]
  dark: boolean
  onSelectComponent: (item: PlanificacionSemanalRow) => void
  highlighted?: boolean
  /** La OP ha salido en la busqueda POR UN COMPONENTE: abre sola su subtabla. */
  autoAbrir?: boolean
  /** Que componentes van resaltados dentro de la subtabla. */
  resalta?: ((item: PlanificacionSemanalRow) => boolean) | null
  /** Firma de la busqueda. Al cambiar se olvidan los despliegues manuales. */
  busquedaNonce?: string
}) {
  // Mismo criterio que en escritorio: manda el despliegue automatico mientras el
  // usuario no toque nada, y un filtro nuevo descarta lo que tocase antes.
  const [manual, setManual] = useState<boolean | null>(null)
  useEffect(() => { setManual(null) }, [busquedaNonce])
  const open = manual ?? !!autoAbrir
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()

  // Mismo ambito y misma clave que la fila de escritorio: lo que se marca en el
  // movil sigue marcado al volver al Gantt ancho, porque es la misma OP.
  const marcas = useMarcasFilas('gantt')
  const claveOp = claveFila(prod.empresa, prod.produccion)
  const marcada = useMarcada(marcas, claveOp)

  const unidades   = Number(prod.unidades)
  const pendientes = Number(prod.unidadesPendientes)
  const avancePct  = pct(unidades - pendientes, unidades)

  const fabricablePct = useMemo(() => {
    if (!items.length) return -1
    const ratios = items.map(r =>
      r.consumoEstimado > 0
        ? Math.min(1, Math.max(0, r.consumoEstimado - Math.max(0, r.pendiente)) / r.consumoEstimado)
        : 1
    )
    return Math.round(Math.min(...ratios) * 100)
  }, [items])

  const roturaCount = items.filter(r => cuentaComoRotura(r)).length
  const hasRotura   = roturaCount > 0

  // Misma chapa que en escritorio y por el mismo motivo: pasado el tope de
  // despliegue automatico la tarjeta queda cerrada, y sin ella no se veria por
  // que esta en la lista. Ver `GanttRow`.
  const coincidenciasComp = useMemo(
    () => (resalta ? items.filter(resalta).length : 0),
    [items, resalta],
  )

  const isDelayed = useMemo(() => {
    const fechaFin = parseDate(prod.fechaFin)
    const today = new Date(); today.setHours(0, 0, 0, 0)
    return !!fechaFin && fechaFin < today && pendientes > 0
  }, [prod.fechaFin, pendientes])

  const d = sd(prod.status)

  const cardBg = hasRotura
    ? (dark ? 'bg-red-950/20 border-red-900/30' : 'bg-red-50/40 border-red-200')
    : isDelayed
      ? (dark ? 'bg-amber-950/20 border-amber-900/30' : 'bg-amber-50/30 border-amber-200')
      : (dark ? 'bg-slate-900 border-white/8' : 'bg-white border-slate-200')

  const txtMain  = dark ? 'text-slate-100' : 'text-slate-800'
  const txtMuted = dark ? 'text-slate-500' : 'text-slate-400'
  const highlightRing = highlighted
    ? (dark ? 'ring-2 ring-sky-500/50' : 'ring-2 ring-sky-400/50')
    : ''

  const stripColor = isDelayed ? 'bg-amber-400' : hasRotura ? 'bg-red-400' : d.bar

  return (
    <div
      id={`gantt-row-${prod.produccion}`}
      className={`rounded-xl border overflow-hidden flex ${cardBg} ${marcada ? (dark ? 'fila-marcada-dark' : 'fila-marcada') : ''} ${highlightRing}`}
    >
      {/* Borde izquierdo de estado */}
      <div className={`w-1 shrink-0 ${stripColor}`} />
      <div className="flex-1 min-w-0">
      <div
        className="px-3 py-2.5 cursor-pointer active:opacity-70 select-none"
        onClick={() => items.length && setManual(!open)}
      >
        {/* Fila 1: estado + ID + badges + unidades */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <BotonMarca marcas={marcas} clave={claveOp} dark={dark} marcada={marcada} />
            <span className={`w-2 h-2 rounded-full shrink-0 mt-0.5 ${d.dot}`} />
            <span className={`text-[12px] font-black truncate ${txtMain}`}>{prod.produccion}</span>
            {/* Sólo con la tarjeta CERRADA: abierta, el resaltado de la
                sublínea ya dice por qué está aquí. */}
            {coincidenciasComp > 0 && !open && (
              <span
                title={t('planning.gantt.componentMatchTip', { count: coincidenciasComp })}
                className={`text-[9px] font-bold px-1 py-px rounded shrink-0 ${
                  dark ? 'bg-sky-900/60 text-sky-300 border border-sky-700/40'
                       : 'bg-sky-100 text-sky-700 border border-sky-200'
                }`}
              >
                ⌕{coincidenciasComp}
              </span>
            )}
            {roturaCount > 0 && (
              <span className="text-[9px] font-bold text-red-500 bg-red-100 border border-red-200 px-1 py-px rounded shrink-0">
                ⚠{roturaCount}
              </span>
            )}
            {isDelayed && (
              <span className={`text-[9px] font-bold px-1 py-px rounded shrink-0 ${
                dark ? 'bg-amber-900/60 text-amber-400 border border-amber-700/40'
                     : 'bg-amber-100 text-amber-700 border border-amber-200'
              }`}>RETRASO</span>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="text-right">
              <div className={`text-[12px] font-black tabular-nums ${txtMain}`}>{fmt(unidades)}</div>
              <div className={`text-[9px] tabular-nums ${txtMuted}`}>{fmt(pendientes)} p</div>
            </div>
            {items.length > 0 && (
              <span className={`text-[10px] ${txtMuted}`}>{open ? '▾' : '▸'}</span>
            )}
          </div>
        </div>

        {/* Fila 2: nombre artículo */}
        <div className={`text-[11px] truncate mt-0.5 ml-4 ${txtMuted}`}>
          {prod.nombreArticulo || fmtArt(prod.articulo)}
          {prod.inventcolorid && ` · ${prod.inventcolorid}${prod.inventstyleid ? ` · ${prod.inventstyleid}` : ''}`}
        </div>

        {/* Fila 3: fechas + almacén + operarios */}
        <div className="flex items-center gap-2 mt-1.5 ml-4 flex-wrap">
          <span className={`text-[10px] tabular-nums ${txtMuted}`}>
            {fmtDate(prod.fechaInicio)} → {fmtDate(prod.fechaFin)}
          </span>
          {prod.inventlocationid && (
            <span className={`text-[9px] font-semibold px-1 py-px rounded ${
              dark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600 border border-slate-200'
            }`}>{prod.inventlocationid}</span>
          )}
          {prod.cantidadOperarios > 0 && (
            <span className={`text-[9px] tabular-nums ${txtMuted}`}>{prod.cantidadOperarios} op.</span>
          )}
        </div>

        {/* Fila 4: barra de progreso */}
        <div className="flex items-center gap-2 mt-2 ml-4">
          <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${dark ? 'bg-slate-700' : 'bg-slate-200'}`}>
            <div
              className={`h-full rounded-full ${isDelayed ? 'bg-amber-400' : d.bar} opacity-80`}
              style={{ width: `${avancePct}%` }}
            />
          </div>
          <span className={`text-[10px] font-semibold tabular-nums w-8 text-right shrink-0 ${txtMuted}`}>{avancePct}%</span>
          {fabricablePct >= 0 && (
            <span className={`text-[9px] tabular-nums shrink-0 ${
              fabricablePct < 60 ? 'text-red-500' : fabricablePct < 90 ? 'text-amber-500' : txtMuted
            }`}>{fabricablePct}%m</span>
          )}
        </div>
      </div>

      {open && <ConsumoSubTable items={items} dark={dark} onSelect={onSelectComponent} resalta={resalta} clavePadre={claveOp} />}
      </div>
    </div>
  )
}

export function MobileRutaSection({ ruta, producciones, planRows, dark, onSelectComponent, highlightedProdId, globalExpanded, autoAbrirProds, resalta, busquedaNonce }: {
  ruta: string
  producciones: ProduccionCabecera[]
  planRows: PlanificacionSemanalRow[]
  dark: boolean
  onSelectComponent: (item: PlanificacionSemanalRow) => void
  highlightedProdId?: string | null
  globalExpanded: boolean
  autoAbrirProds?: Set<string>
  resalta?: ((item: PlanificacionSemanalRow) => boolean) | null
  busquedaNonce?: string
}) {
  const [expanded, setExpanded] = useState(globalExpanded)

  useEffect(() => { setExpanded(globalExpanded) }, [globalExpanded])

  // Un grupo cerrado a mano esconderia las coincidencias de la busqueda nueva.
  useEffect(() => {
    if (busquedaNonce) setExpanded(true)
  }, [busquedaNonce])

  useEffect(() => {
    if (highlightedProdId && producciones.some(p => p.produccion === highlightedProdId)) {
      setExpanded(true)
    }
  }, [highlightedProdId, producciones])

  const planByProd = useMemo(() => {
    const map = new Map<string, PlanificacionSemanalRow[]>()
    planRows.forEach(r => { const l = map.get(r.prodid) ?? []; l.push(r); map.set(r.prodid, l) })
    return map
  }, [planRows])

  const totalU     = producciones.reduce((s, p) => s + Number(p.unidades), 0)
  const completedU = producciones.reduce((s, p) => s + Number(p.unidades) - Number(p.unidadesPendientes), 0)
  const avPct      = pct(completedU, totalU)
  const prodsRoturaCount = new Set(planRows.filter(r => cuentaComoRotura(r)).map(r => r.prodid)).size

  const hdrBg  = dark ? 'bg-slate-800/70' : 'bg-slate-100/80'
  const txtM   = dark ? 'text-slate-100'  : 'text-slate-800'
  const txtMut = dark ? 'text-slate-400'  : 'text-slate-500'
  const border = dark ? 'border-white/8'  : 'border-slate-200'

  return (
    <div className={`rounded-xl border overflow-hidden ${dark ? 'border-white/8 bg-slate-900' : 'border-slate-200 bg-slate-50 shadow-sm'}`}>
      <button
        onClick={() => setExpanded(v => !v)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors border-b ${hdrBg} ${border}`}
      >
        <span className={`text-[11px] w-3 shrink-0 ${txtMut}`}>{expanded ? '▾' : '▸'}</span>
        <span className={`text-[13px] font-black flex-1 truncate ${txtM}`}>{ruta}</span>
        <span className={`text-[11px] tabular-nums shrink-0 ${txtMut}`}>{producciones.length} prod.</span>
        {prodsRoturaCount > 0 && (
          <span className="text-[10px] font-bold text-red-500 shrink-0">⚠{prodsRoturaCount}</span>
        )}
        <div className="flex items-center gap-1.5 shrink-0">
          <div className={`w-14 h-1.5 rounded-full overflow-hidden ${dark ? 'bg-slate-700' : 'bg-slate-300'}`}>
            <div
              className={`h-full rounded-full ${avPct >= 75 ? 'bg-emerald-400' : avPct >= 40 ? 'bg-sky-400' : 'bg-slate-400'}`}
              style={{ width: `${avPct}%` }}
            />
          </div>
          <span className={`text-[10px] tabular-nums ${txtMut}`}>{avPct}%</span>
        </div>
      </button>

      {expanded && (
        <div className="p-2 flex flex-col gap-2">
          {producciones.map(prod => (
            <OrderCard
              key={prod.produccion}
              prod={prod}
              items={planByProd.get(prod.produccion) ?? []}
              dark={dark}
              onSelectComponent={onSelectComponent}
              highlighted={highlightedProdId === prod.produccion}
              autoAbrir={autoAbrirProds?.has(prod.produccion)}
              resalta={resalta}
              busquedaNonce={busquedaNonce}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export function MobileLoadingSkeleton({ dark }: { dark: boolean }) {
  const base  = dark ? 'bg-slate-700/60' : 'bg-slate-200'
  const hdr   = dark ? 'bg-slate-800'    : 'bg-slate-100'
  const card  = dark ? 'bg-slate-800/50 border-white/5' : 'bg-slate-50 border-slate-100'
  const outer = dark ? 'border-white/8 bg-slate-900'    : 'border-slate-200 bg-white'

  return (
    <div className="flex flex-col gap-2 animate-pulse">
      {[8, 5, 3].map((cards, gi) => (
        <div key={gi} className={`rounded-xl border overflow-hidden ${outer}`}>
          <div className={`h-11 ${hdr}`} />
          <div className="p-2 flex flex-col gap-2">
            {Array.from({ length: cards > 3 ? 3 : cards }).map((_, ci) => (
              <div key={ci} className={`rounded-xl border p-3 ${card}`}>
                <div className="flex justify-between mb-2">
                  <div className={`h-3 w-28 rounded ${base}`} />
                  <div className={`h-3 w-10 rounded ${base}`} />
                </div>
                <div className={`h-2.5 w-44 rounded mb-2 ${base}`} />
                <div className="flex gap-2 mb-3">
                  <div className={`h-2 w-20 rounded ${base}`} />
                  <div className={`h-2 w-10 rounded ${base}`} />
                </div>
                <div className={`h-1.5 w-full rounded ${base}`} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// Re-export fmtUds so callers don't need a separate import
export { fmtUds }
